import { CardTable } from 'common/components/datatable/Table'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useActions from 'common/hooks/useActions'
import useBoolean from 'common/hooks/useBoolean'
import useTitles from 'common/hooks/useTitles'
import { isUndefined } from 'lodash'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import { Card, CardContent, Dialog, DialogContent, Grid } from '@mui/material'
import Checkbox from 'common/components/form/Checkbox'
import Input from 'common/components/form/Input'
import PropTypes from 'prop-types'
import { NEW } from 'common/components/action/ActionConstant'
import { hasPermission } from 'common/utils/HabilitationUtil'
import { selectServices, ServicesActions } from 'supervision/services/ServicesSlice'

const PopinService = ({
    isOpen = false,
    close = () => { },
    reload = () => { },
    service = {},
}) => {
    const dispatch = useDispatch()

    const {
        value: isLoading,
        setTrue: loading,
        setFalse: notLoading,
    } = useBoolean(false)

    const {
        value: isChainCreate,
        toggle: toggleChainCreate,
    } = useBoolean(false)

    const [name, setName] = useState()
    const [apiName, setApiName] = useState()
    const [gitlabName, setGitlabName] = useState()
    const [refs, setRefs] = useState()
    const [instanceType, setInstanceType] = useState()

    useEffect(() => {
        if (isOpen) {
            setName(service.name)
            setApiName(service.apiName)
            setGitlabName(service.gitlabName)
            setRefs(service.refs)
            setInstanceType(service.instanceType)
        }
    }, [service.name, service.apiName, service.gitlabName, service.isDefault, isOpen, service.refs, service.instanceType])

    const resetAllField = () => {
        setName()
        setApiName()
        setGitlabName()
        setRefs()
        setInstanceType()
    }

    const dispatchPromise = () => {
        if (isUndefined(service.id)) {
            return dispatch(ServicesActions.create({ id: -1, name, apiName, gitlabName, instanceType }))
        }
        return dispatch(ServicesActions.update({ id: service.id, name, apiName, gitlabName, instanceType }))
    }

    const validate = () => {
        loading()
        dispatchPromise()
            .then(res => {
                if (!res) {
                    return
                }
                reload()
                if (!isChainCreate) {
                    close()
                } else {
                    resetAllField()
                }
            })
            .finally(notLoading)
    }

    return (
        <Dialog
            open={isOpen}
            maxWidth='lg'
        >
            <BasicDialogTitle onClose={close}>
                Service
            </BasicDialogTitle>
            <DialogContent style={{ overflowX: 'hidden' }}>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center'>
                            <Grid size={6}>
                                <Input
                                    label={'Nom'}
                                    value={name}
                                    onChange={setName}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Nom de l\'api'}
                                    value={apiName}
                                    onChange={setApiName}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Nom sur gitlab'}
                                    value={gitlabName}
                                    onChange={setGitlabName}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Références (séparé par un ;)'}
                                    value={refs}
                                    onChange={setRefs}
                                />
                            </Grid>
                            <Grid size={6}>
                                <Input
                                    label={'Type instance'}
                                    value={instanceType}
                                    onChange={setInstanceType}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isUndefined(service.id) ? 'Créer' : 'Modifier'}
                onValidate={validate}
                isLoading={isLoading}
            >
                {
                    isUndefined(service.id) && (
                        <Checkbox
                            label='Saisir un autre service'
                            checked={isChainCreate}
                            onToggle={toggleChainCreate}
                            labelStyle={{ color: '#555', fontWeight: 'normal' }}
                        />
                    )
                }
            </BasicDialogAction>
        </Dialog>
    )
}

PopinService.propTypes = {
    isOpen: PropTypes.bool,
    close: PropTypes.func,
    reload: PropTypes.func,
    service: PropTypes.shape({
        name: PropTypes.string,
        apiName: PropTypes.string,
        gitlabName: PropTypes.string,
        isDefault: PropTypes.bool,
    }),
}

const ServicesApp = () => {
    const dispatch = useDispatch()
    const {
        user,
        status,
    } = useSelector(store => ({
        user: store.app.user,
        status: store.supervision.services.status,
    }), shallowEqual)
    const services = useSelector(selectServices, shallowEqual)

    const {
        value: isOpenPopin,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: openConfirm,
        setFalse: closeConfirm,
    } = useBoolean(false)

    useTitles(() => [{
        label: 'Service',
        href: '/service',
    }], [])

    useEffect(() => {
        dispatch(ServicesActions.fetch())
    }, [dispatch])

    const [selectedService, setSelectedService] = useState()

    const formattedServices = services.map(service => ({
        id: service.id,
        name: { value: service.name },
        apiName: { value: service.apiName },
        gitlabName: { value: service.gitlabName },
        refs: { value: service.refs },
        instanceType: { value: service.instanceType },
    }))

    useActions(() => [{
        type: NEW,
        onClick: openPopin,
        displayed: hasPermission(user, 'services', 'create'),
    }], [])

    const service = useMemo(() => {
        return services.find(i => i.id === selectedService) ?? {}
    }, [services, selectedService])

    const onDelete = row => {
        setSelectedService(row.id)
        openConfirm()
    }

    const onEdit = row => {
        setSelectedService(row.id)
        openPopin()
    }

    return (
        <div style={{ paddingBottom: '50px' }}>
            <CardTable
                title={'Services'}
                color='#161832'

                rows={formattedServices}
                headers={[{ key: 'name', value: 'Nom' }, { key: 'apiName', value: 'Nom de l\'api' }, { key: 'gitlabName', value: 'Nom sur gitlab' }, { key: 'refs', value: 'Références' }, { key: 'instanceType', value: 'Type instance' }]}

                actions={[{
                    icon: 'replay',
                    onClick: () => {
                        dispatch(ServicesActions.reset())
                        dispatch(ServicesActions.fetch())
                    },
                    tooltip: 'Recharger',
                }]}

                lineActions={[
                    { icon: 'delete', onClick: onDelete, displayed: hasPermission(user, 'services', 'delete') },
                    { icon: 'edit', onClick: onEdit, displayed: hasPermission(user, 'services', 'update') },
                ]}
                status={status}
                rowsPerPageDefault={50}
            />
            <PopinService
                isOpen={isOpenPopin}
                close={() => {
                    setSelectedService()
                    closePopin()
                }}
                reload={() => dispatch(ServicesActions.fetch())}
                service={service}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer ce service ?'
                onValidate={() => {
                    dispatch(ServicesActions.delete(selectedService)).finally(() => {
                        dispatch(ServicesActions.fetch())
                        setSelectedService()
                        closeConfirm()
                    })
                }}
                onClose={closeConfirm}
            />
        </div>
    )
}

export default ServicesApp